import { daysBetween } from './dates.ts';
import type { Cents } from './money.ts';

/** Percentage change; null ("N/D") when the base is zero — never 0, Infinity or NaN (plano §9.1). */
export function percentChange(current: number, base: number): number | null {
  if (base === 0) return null;
  return (current / base - 1) * 100;
}

export function ratio(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : numerator / denominator;
}

/** Average ticket in centavos, rounded half-even; null when nobody paid. */
export function averageTicketCents(revenueCents: Cents, payingQty: number): Cents | null {
  if (payingQty === 0) return null;
  const q = Math.trunc(revenueCents / payingQty);
  const r = revenueCents - q * payingQty;
  const twice = Math.abs(r) * 2;
  if (twice > payingQty || (twice === payingQty && Math.abs(q) % 2 === 1)) return q + Math.sign(revenueCents);
  return q;
}

export type Comparability = 'COMPARABLE' | 'NOT_COMPARABLE' | 'NO_BASE';

export interface PeriodCoverage {
  /** Days in the compared interval. */
  days: number;
  /** Days in the interval whose movement counts in the basis (approved; on the provisional basis also closed). */
  approvedDays: number;
}

/**
 * Which movements the coverage counted: on the provisional basis a closed or checked day counts too, so the reason
 * says "dias contabilizados" instead of "dias aprovados" (CMP-05).
 */
export type CoverageBasis = 'approved' | 'provisional';

const COUNTED_DAYS: Record<CoverageBasis, { days: string; movement: string }> = {
  approved: { days: 'dias aprovados', movement: 'movimento aprovado' },
  provisional: { days: 'dias contabilizados', movement: 'movimento contabilizado' },
};

export interface Comparison {
  current: number;
  base: number | null;
  absoluteChange: number | null;
  percentChange: number | null;
  comparability: Comparability;
  reason: string | null;
}

/**
 * DOCX §12 rule: compare only the same revenue concept over the same reference interval, and flag when
 * periods are not comparable. Callers pass totals already restricted to the same day interval
 * (e.g. 1..17 of both months when the current month is open up to day 17).
 */
export function compare(
  current: number,
  currentCoverage: PeriodCoverage,
  base: number,
  baseCoverage: PeriodCoverage,
  /** Both periods are complete calendar months (a 31-day month vs a 30-day month is legitimate). */
  fullMonths = false,
  basis: CoverageBasis = 'approved',
): Comparison {
  const words = COUNTED_DAYS[basis];
  if (baseCoverage.approvedDays === 0) {
    return {
      current,
      base: null,
      absoluteChange: null,
      percentChange: null,
      comparability: 'NO_BASE',
      reason: `sem ${words.movement} no período de comparação`,
    };
  }
  const incomplete =
    baseCoverage.approvedDays < baseCoverage.days || currentCoverage.approvedDays < currentCoverage.days;
  // Different interval lengths are not the same reference period (finance review M5), e.g. 1–30 March
  // vs 1–28 February when the base month is shorter.
  if (!incomplete && !fullMonths && currentCoverage.days !== baseCoverage.days) {
    return {
      current,
      base,
      absoluteChange: current - base,
      percentChange: percentChange(current, base),
      comparability: 'NOT_COMPARABLE',
      reason: `períodos de tamanhos diferentes: ${currentCoverage.days} × ${baseCoverage.days} dias`,
    };
  }
  return {
    current,
    base,
    absoluteChange: current - base,
    percentChange: percentChange(current, base),
    comparability: incomplete ? 'NOT_COMPARABLE' : 'COMPARABLE',
    reason: incomplete
      ? `cobertura incompleta: atual ${currentCoverage.approvedDays}/${currentCoverage.days} ${words.days}, base ${baseCoverage.approvedDays}/${baseCoverage.days}`
      : null,
  };
}

export interface HealthScoreInput {
  reconciledRatio: number | null;
  closedOnTimeRatio: number | null;
  divergenceFreeRatio: number | null;
  approvalsOnTimeRatio: number | null;
  integrityRatio: number | null;
  syncOkRatio: number | null;
}

/** DOCX §10.1 suggested weights; parameterizable. Internal operational indicator, not a financial opinion. */
export const DEFAULT_HEALTH_WEIGHTS = {
  reconciledRatio: 25,
  closedOnTimeRatio: 20,
  divergenceFreeRatio: 20,
  approvalsOnTimeRatio: 15,
  integrityRatio: 10,
  syncOkRatio: 10,
} as const satisfies Record<keyof HealthScoreInput, number>;

export type HealthComponentKey = keyof HealthScoreInput;
export type HealthWeights = Record<HealthComponentKey, number>;

/** Component order of DOCX §10.1 (the order the panel lists them). */
export const HEALTH_COMPONENTS: readonly HealthComponentKey[] = [
  'reconciledRatio',
  'closedOnTimeRatio',
  'divergenceFreeRatio',
  'approvalsOnTimeRatio',
  'integrityRatio',
  'syncOkRatio',
];

export interface HealthComponent {
  key: HealthComponentKey;
  weight: number;
  /** Measured ratio clamped to 0–1; null when the component could not be measured. */
  ratio: number | null;
  /** Points this component adds to the 0–100 score (weight renormalised over the measured components). */
  points: number | null;
}

export interface HealthBreakdown {
  score: number | null;
  /** Sum of the weights of the measured components. */
  measuredWeight: number;
  totalWeight: number;
  /** measuredWeight / totalWeight; null when every weight is zero. */
  measuredShare: number | null;
  components: HealthComponent[];
}

/**
 * Weighted 0–100 score over the components that could be measured, with each component's contribution
 * (DOCX §10.1: "componentes detalhados"). Unmeasured components are left out and the remaining weights are
 * renormalised, so the score is null only when nothing could be measured (or every weight is zero).
 */
export function healthBreakdown(
  input: HealthScoreInput,
  weights: HealthWeights = DEFAULT_HEALTH_WEIGHTS,
): HealthBreakdown {
  let weighted = 0;
  let measured = 0;
  let total = 0;
  for (const key of HEALTH_COMPONENTS) {
    const w = Math.max(0, weights[key]);
    total += w;
    const v = input[key];
    if (v === null) continue;
    weighted += Math.min(1, Math.max(0, v)) * w;
    measured += w;
  }
  const components = HEALTH_COMPONENTS.map((key) => {
    const w = Math.max(0, weights[key]);
    const v = input[key];
    const r = v === null ? null : Math.min(1, Math.max(0, v));
    return {
      key,
      weight: w,
      ratio: r,
      points: r === null || measured === 0 ? null : (r * w * 100) / measured,
    };
  });
  return {
    score: measured === 0 ? null : Math.round((weighted / measured) * 100),
    measuredWeight: measured,
    totalWeight: total,
    measuredShare: total === 0 ? null : measured / total,
    components,
  };
}

/** Weighted 0–100 score over the components that could be measured; null when none could. */
export function healthScore(
  input: HealthScoreInput,
  weights: HealthWeights = DEFAULT_HEALTH_WEIGHTS,
): { score: number | null; measuredWeight: number } {
  const b = healthBreakdown(input, weights);
  return { score: b.score, measuredWeight: b.measuredWeight };
}

/** Statuses after the day was closed by the operator (DOCX §23 "fechamentos completos"). */
export const CLOSED_MOVEMENT_STATUSES = ['CLOSED', 'CHECKED', 'APPROVED', 'REOPEN_PENDING'] as const;

export interface ClosingCounts {
  /** Calendar days in the interval (each day needs one closing). */
  expectedDays: number;
  /** Days whose movement was closed (closed, checked, approved or with a reopen request pending). */
  completeDays: number;
  /** expectedDays − completeDays: drafts, returned, reopened and days without any movement. */
  pendingDays: number;
  /** Days without a movement (or with only a cancelled one) — counted inside pendingDays. */
  withoutMovementDays: number;
}

/**
 * Complete × pending closings over [from, to]. A day with no movement counts as pending (backlog item C2):
 * the unit operates every day, so a missing launch is a missing closing, not an absent one.
 */
export function closingCounts(
  movements: { operationalDate: string; status: string }[],
  from: string,
  to: string,
): ClosingCounts {
  if (to < from) return { expectedDays: 0, completeDays: 0, pendingDays: 0, withoutMovementDays: 0 };
  const expectedDays = daysBetween(from, to) + 1;
  const live = new Map<string, string>();
  for (const m of movements) {
    if (m.operationalDate < from || m.operationalDate > to || m.status === 'CANCELLED') continue;
    live.set(m.operationalDate, m.status);
  }
  const closed = new Set<string>(CLOSED_MOVEMENT_STATUSES);
  const completeDays = [...live.values()].filter((s) => closed.has(s)).length;
  return {
    expectedDays,
    completeDays,
    pendingDays: expectedDays - completeDays,
    withoutMovementDays: expectedDays - live.size,
  };
}

/** Mean of durations given in milliseconds, in hours; null for an empty list (never 0). */
export function meanHours(durationsMs: number[]): number | null {
  if (durationsMs.length === 0) return null;
  return durationsMs.reduce((a, d) => a + d, 0) / durationsMs.length / 3_600_000;
}

export type ReconciliationBucket = 'RECONCILED' | 'PENDING' | 'DIVERGENT';

/**
 * Report grouping of a reconciliation status (DOCX §11 "conciliados, pendentes e divergentes"). Any status the
 * grouping does not know yet is reported as pending, never silently as reconciled.
 */
export function reconciliationBucket(status: string): ReconciliationBucket {
  if (status === 'MATCHED' || status === 'TOLERABLE_DIFFERENCE' || status === 'RESOLVED') return 'RECONCILED';
  if (status === 'DIVERGENT') return 'DIVERGENT';
  return 'PENDING';
}

export type TicketComparison = Omit<Comparison, 'current'> & { current: number | null };

/**
 * Average ticket of two periods compared under the same rules as revenue (DOCX §12 "evolução de ticket médio").
 * A period without paying vehicles has no ticket: the variation is N/D, never 0.
 */
export function compareTicket(
  current: { revenueCents: Cents; payingQty: number; coverage: PeriodCoverage },
  base: { revenueCents: Cents; payingQty: number; coverage: PeriodCoverage },
  fullMonths = false,
  basis: CoverageBasis = 'approved',
): TicketComparison {
  const cur = averageTicketCents(current.revenueCents, current.payingQty);
  const old = averageTicketCents(base.revenueCents, base.payingQty);
  if (old === null || base.coverage.approvedDays === 0) {
    return {
      current: cur,
      base: null,
      absoluteChange: null,
      percentChange: null,
      comparability: 'NO_BASE',
      reason:
        base.coverage.approvedDays === 0
          ? `sem ${COUNTED_DAYS[basis].movement} no período de comparação`
          : 'sem pagantes no período de comparação',
    };
  }
  if (cur === null) {
    return {
      current: null,
      base: old,
      absoluteChange: null,
      percentChange: null,
      comparability: 'NOT_COMPARABLE',
      reason: 'sem pagantes no período atual',
    };
  }
  return compare(cur, current.coverage, old, base.coverage, fullMonths, basis);
}
